//---------------------------------------------------------------------------------------------------
//  ?                           WEBSOCKET PROVIDER
//  @author         :  DeeTrade Team  
//  @createdOn      :  06/19/2025
//  @description    :  React context provider for ultra-fast WebSocket state management
//---------------------------------------------------------------------------------------------------

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import WebSocketManager, { DELIVERY_STALL_THRESHOLD_MS } from '../websocket/WebSocketManager';
import { loadExchangeStatusFromApi, updateExchangeStatus } from '../utils/exchangeHours';

/**
 * #44/V12b: the ONE precedence ladder for `connectionHealth`.
 *
 * //! V12b F3: `connectionHealth` has a dozen writers on three different clocks
 * //! (event handlers, the 10s stall watchdog, the 2s reconciler) and NOTHING
 * //! arbitrated between them — which is the root cause the investigation bottoms
 * //! out at. The first fix gated ONE branch of ONE writer, so the two
 * //! unconditional clears in handleAuthSuccess/handleReconnected still wiped the
 * //! stall banner on every (re)auth — including the re-auth caused by the stall
 * //! watchdog's OWN self-heal, i.e. once per heal cycle for a whole outage.
 * //! Every writer that wants to report "healthy" now goes through here instead
 * //! of assigning 'connected' directly.
 *
 * Ladder (worst wins): offline > degraded > stalled > connected.
 *   - `degraded` outranks `stalled`: the fast-retry budget being spent is a
 *     louder, more actionable failure than tick silence.
 *   - `stalled` outranks `connected`: transport-up is NOT proof of delivery.
 *     That conflation IS the V12b defect.
 *
 * //! DERIVES from ground truth — it does NOT latch on `prev`. The first cut of
 * //! this helper returned `prev` when prev was 'degraded'/'offline', which made
 * //! both ABSORBING: nothing anywhere transitioned out of them, so a red
 * //! "Live data down" pill would persist for ever on a fully recovered app. The
 * //! old unconditional `setConnectionHealth('connected')` in handleAuthSuccess
 * //! had been the de-facto exit. Caught by qa-deetrade-invariants. The manager
 * //! already clears its own isDegraded flag on re-auth and reports it here, so
 * //! reading it is both correct and free — a latch was never needed.
 */
//! V12B-05: how long to let queued frames drain after a tab returns to the
//! foreground, before judging delivery health. Long enough that a throttle
//! artefact cannot masquerade as a stall; far shorter than the ~60s a throttled
//! watchdog would take to notice.
const VISIBILITY_RECHECK_DELAY_MS = 1500;

const resolveHealth = () => {
  //! device-level truth first: no network beats every socket-level judgement.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  const h = WebSocketManager.getDeliveryHealth();
  if (h.isDegraded) return 'degraded';
  if (h.stalled) return 'stalled';
  return 'connected';
};

// Create WebSocket context
export const WebSocketContext = createContext(null);

/**
 * WebSocket Provider for ultra-fast trading platform
 * Manages app-wide WebSocket state and user identity
 */
export const WebSocketProvider = ({ children, authToken, userId, autoConnect = true }) => {
  // Global WebSocket state
  const [connectionState, setConnectionState] = useState('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  //! True only after WS auth_success — use this (not isConnected) for subscription effects
  //! to guarantee WebSocketManager.isAuthenticated === true when subscribeTopic is called.
  const [isWsReady, setIsWsReady] = useState(false);
  //! #31/exchange-session-ssot R5.2: ONE shared `exchange-status.update` subscription
  //! owned by this provider, fanned out via context — MarketStatusDashboard (and any
  //! future consumer) reads this instead of subscribing to the topic itself.
  const [lastExchangeStatusUpdate, setLastExchangeStatusUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [userIdentity, setUserIdentity] = useState(null);
  const [globalMetrics, setGlobalMetrics] = useState({
    latency: null,
    messageCount: 0,
    reconnectCount: 0,
    uptime: 0
  });

  // Connection management
  const [connectionConfig, setConnectionConfig] = useState({
    enableBinary: true,
    enableCompression: true,
    heartbeatInterval: 30000
  });
  const [reconnectError, setReconnectError] = useState(null);
  //! #44: derived connection-health for the LiveDataStatus. One of:
  //! 'connected' | 'reconnecting' | 'degraded' | 'stalled' | 'offline'.
  const [connectionHealth, setConnectionHealth] = useState('connected');
  //! #44: only surface the disconnect banner once a live socket has actually
  //! existed — avoids a banner flash during the very first connect on load.
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);

  //! V12B-05: pending foreground delivery-health re-check, so a rapid
  //! hide/show/hide cannot leave a stale timer that fires against old state.
  const visibilityRecheckRef = useRef(null);

  /**
   * Get WebSocket-specific token with NEW session_id from auth service
   */
  const getWebSocketToken = useCallback(async (accessToken, userId) => {
    console.log('🎫 getWebSocketToken called with userId:', userId, 'token present:', !!accessToken);
    
    try {
      const rawAuthBaseUrl = process.env.REACT_APP_AUTH_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const AUTH_BASE_URL = rawAuthBaseUrl.replace(/\/api\/v1\/?$/, '');
      console.log('🌐 Using Auth Base URL:', AUTH_BASE_URL, 'raw:', rawAuthBaseUrl);
      
      // NEW FLOW: Send user_id in query params and access_token in Authorization header
      const queryParams = new URLSearchParams({
        user_id: userId.toString()
      });
      
      const wsTokenEndpoint = `${AUTH_BASE_URL}/api/v1/ws-auth/issue-socket-token?${queryParams}`;
      
      console.log(`🔍 Requesting WebSocket token for user_id: ${userId} with validated access token`);
      
      const wsTokenResponse = await fetch(wsTokenEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }
      });

      if (wsTokenResponse.ok) {
        const tokenData = await wsTokenResponse.json();
        console.log('✅ WebSocket token obtained successfully with NEW session_id:', tokenData.session_id);
        
        // Store the new WebSocket session_id for connection identity
        const websocketToken = tokenData.access_token || tokenData.token;
        const websocketSessionId = tokenData.session_id;
        
        console.log('🆔 Using NEW WebSocket session_id:', websocketSessionId);
        
        return {
          token: websocketToken,
          session_id: websocketSessionId,
          expires_in: tokenData.expires_in
        };
      } else {
        const errorText = await wsTokenResponse.text();
        console.error(`❌ WebSocket token request failed:`, wsTokenResponse.status, errorText);
        throw new Error(`Failed to get WebSocket token: ${wsTokenResponse.status} ${errorText}`);
      }
      
    } catch (error) {
      console.error('❌ Error in getWebSocketToken:', error);
      throw error; // Don't fall back, throw the error so we can see what's wrong
    }
  }, []);

  /**
   * Initialize WebSocket connection with user identity
   */
  const initializeConnection = useCallback(async () => {
    console.log('🔧 initializeConnection called with:', { userId, authToken: authToken ? 'present' : 'missing' });
    
    if (userId && authToken) {
      // Check if we're already connected and authenticated
      const isAlreadyConnected = WebSocketManager.connectionState === 'connected';
      const isAlreadyAuthenticated = WebSocketManager.isAuthenticated;
      const currentToken = WebSocketManager.authToken;

      // If already connected, authenticated, and token hasn't changed, skip reconnection
      if (isAlreadyConnected && isAlreadyAuthenticated && currentToken === authToken) {
          console.log('✅ WebSocket already connected and authenticated, skipping initialization');
          //! Sync isWsReady — auth_success won't fire again on this mount, so set directly
          setIsWsReady(true);
          return;
      }

      try {
        // Get WebSocket-specific token with NEW session_id
        const wsTokenData = await getWebSocketToken(authToken, userId);
        
        console.log('🆔 WebSocket token data received:', {
          hasToken: !!wsTokenData.token,
          hasSessionId: !!wsTokenData.session_id,
          sessionId: wsTokenData.session_id
        });
        
        // Set user identity in WebSocket manager with WebSocket token and session_id
        WebSocketManager.setUserIdentity(userId, wsTokenData.token, wsTokenData.session_id);
        
        // Connect if auto-connect is enabled and not already connected
        if (autoConnect && !isAlreadyConnected) {
          await WebSocketManager.connect();
        }
      } catch (error) {
        console.error('WebSocket initialization failed:', error);
        setError(error);
      }
    } else {
      console.log('❌ Missing userId or authToken for WebSocket initialization');
    }
  }, [userId, authToken, autoConnect, getWebSocketToken]);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(async () => {
    try {
      await WebSocketManager.connect();
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setError(error);
    }
  }, []);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    WebSocketManager.disconnect();
  }, []);

  /**
   * Update connection configuration
   */
  const updateConfig = useCallback((newConfig) => {
    setConnectionConfig(prev => ({ ...prev, ...newConfig }));
    
    // Apply configuration to WebSocket manager
    if (WebSocketManager.config) {
      Object.assign(WebSocketManager.config, newConfig);
    }
  }, []);

  /**
   * Set up WebSocket event listeners
   */
  useEffect(() => {
    const handleConnection = (data) => {
      setConnectionState(data.state);
      setIsConnected(data.state === 'connected');
      setError(null);

      if (data.state === 'connected') {
        setUserIdentity(WebSocketManager.userIdentity);
      } else if (data.state === 'connecting' || data.state === 'disconnected') {
        //! #44: transport dropped/attempting — show "reconnecting" (banner is
        //! still gated on hasConnectedOnce so first-load connect stays quiet).
        setConnectionHealth((prev) => (prev === 'degraded' ? 'degraded' : 'reconnecting'));
      }
    };

    const handleAuthSuccess = () => {
      setIsWsReady(true);
      //! #44: transport is fully up — remember we've connected.
      setHasConnectedOnce(true);
      //! V12b F3: a successful (re)auth proves TRANSPORT health only. It says
      //! nothing about whether ticks are arriving, and the stall watchdog's own
      //! self-heal reconnect lands right here — so assigning 'connected'
      //! unconditionally re-erased the banner the fix had just made stick.
      setConnectionHealth(resolveHealth);
      setReconnectError(null);
    };

    //! #44: manager emits 'reconnected' on every (re)auth — same clear path,
    //! and the same V12b F3 hazard. Goes through the precedence ladder too.
    const handleReconnected = () => {
      setConnectionHealth(resolveHealth);
      setReconnectError(null);
    };

    const handleDisconnection = (data) => {
      setConnectionState('disconnected');
      setIsConnected(false);
      setIsWsReady(false);
      setUserIdentity(null);
      setConnectionHealth((prev) => (prev === 'degraded' ? 'degraded' : 'reconnecting'));
    };

    const handleError = (error) => {
      setError(error);
      setConnectionState('error');
      setIsConnected(false);
      setIsWsReady(false);
      setConnectionHealth((prev) => (prev === 'degraded' ? 'degraded' : 'reconnecting'));
    };

    //! #44: fast-retry budget spent — now in slow indefinite retry. Escalate
    //! the banner from "reconnecting" to the louder "degraded / refresh" state.
    const handleReconnectDegraded = (data) => {
      setConnectionHealth('degraded');
      setReconnectError(data);
      console.warn('⚠️ [WS] entered degraded slow-retry:', data);
    };

    const handleReconnect = (data) => {
      setGlobalMetrics(prev => ({
        ...prev,
        reconnectCount: prev.reconnectCount + 1
      }));
    };

    const handleIdentityReady = (hierarchicalIdentity) => {
      console.log('🏗️ Hierarchical identity ready:', hierarchicalIdentity);
      setUserIdentity(prev => ({
        ...prev,
        ...hierarchicalIdentity,
        hierarchical: true
      }));
    };

    const handleIdentityError = (identityError) => {
      console.error('🚫 Hierarchical identity error:', identityError);
      setError(new Error(`Identity setup failed: ${identityError.message}`));
    };

    //! #44: legacy event — the manager no longer permanently gives up, but keep
    //! this listener so any residual emitter still surfaces (as degraded, not a
    //! silent death).
    const handleReconnectFailed = (data) => {
      setReconnectError(data);
      setConnectionHealth('degraded');
      console.error('WebSocket reconnect failed:', data);
    };

    const handleTokenRefreshNeeded = async (data) => {
      console.log('🔄 Token refresh needed for reconnect:', data.reason);
      try {
        // Always get the LATEST token from localStorage to avoid stale closures
        const currentAuthToken = localStorage.getItem('accessToken') || authToken;
        
        if (userId && currentAuthToken) {
          // Get fresh WebSocket token
          const wsTokenData = await getWebSocketToken(currentAuthToken, userId);
          
          // Update WebSocket manager with fresh token
          WebSocketManager.setUserIdentity(userId, wsTokenData.token, wsTokenData.session_id);
          
          console.log('✅ Token refreshed for reconnect');
        } else {
          console.warn('⚠️ Cannot refresh token: missing userId or authToken');
        }
      } catch (error) {
        console.error('❌ Failed to refresh token for reconnect:', error);
        setError(error);
      }
    };

    // Register event listeners
    WebSocketManager.on('auth_success', handleAuthSuccess);
    WebSocketManager.on('reconnected', handleReconnected);
    WebSocketManager.on('connection', handleConnection);
    WebSocketManager.on('disconnection', handleDisconnection);
    WebSocketManager.on('error', handleError);
    WebSocketManager.on('reconnect', handleReconnect);
    WebSocketManager.on('reconnect_degraded', handleReconnectDegraded);
    WebSocketManager.on('identity_ready', handleIdentityReady);
    WebSocketManager.on('identity_error', handleIdentityError);
    WebSocketManager.on('reconnect_failed', handleReconnectFailed);
    WebSocketManager.on('token_refresh_needed', handleTokenRefreshNeeded);

    return () => {
      WebSocketManager.off('auth_success', handleAuthSuccess);
      WebSocketManager.off('reconnected', handleReconnected);
      WebSocketManager.off('connection', handleConnection);
      WebSocketManager.off('disconnection', handleDisconnection);
      WebSocketManager.off('error', handleError);
      WebSocketManager.off('reconnect', handleReconnect);
      WebSocketManager.off('reconnect_degraded', handleReconnectDegraded);
      WebSocketManager.off('identity_ready', handleIdentityReady);
      WebSocketManager.off('identity_error', handleIdentityError);
      WebSocketManager.off('reconnect_failed', handleReconnectFailed);
      WebSocketManager.off('token_refresh_needed', handleTokenRefreshNeeded);
    };
  }, [userId, authToken, getWebSocketToken]);

  /**
   * Initialize connection when auth changes
   */
  useEffect(() => {
    console.log('🔍 useEffect for initializeConnection triggered');
    console.log('🔍 Current state:', { userId, authToken: authToken ? 'present' : 'missing', autoConnect });
    console.log('🔍 DEBUG: userId type:', typeof userId, 'value:', userId);
    console.log('🔍 DEBUG: authToken type:', typeof authToken, 'length:', authToken?.length);
    console.log('🔍 DEBUG: autoConnect:', autoConnect);
    initializeConnection();
  }, [initializeConnection, userId, authToken, autoConnect]);

  /**
   * Update global metrics periodically
   */
  useEffect(() => {
    if (isConnected) {
      const metricsInterval = setInterval(() => {
        const metrics = WebSocketManager.getMetrics();
        setGlobalMetrics({
          latency: metrics.lastLatency,
          messageCount: metrics.messagesReceived + metrics.messagesSent,
          reconnectCount: metrics.reconnectCount,
          uptime: metrics.uptime,
          avgLatency: metrics.avgLatency
        });
      }, 1000);

      return () => clearInterval(metricsInterval);
    }
  }, [isConnected]);

  /**
   * Handle page visibility changes.
   *
   * //! F9: this used to claim it "pauses non-critical subscriptions". It never
   * //! did — the pause list named topics that do not exist in this app, so the
   * //! loop matched nothing. Subscriptions stream to hidden tabs, and always
   * //! have; what stops is the BROWSER running our JS (Chrome intensive
   * //! throttling, measured at V12B-05). We only track the absence now.
   */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        //--> Tab hidden. Nothing is unsubscribed; we just start the clock so a
        //--> consumer can tell how much data it missed. See #50 (gap backfill).
        WebSocketManager.markBackgrounded();

      } else {
        // Tab returning to foreground
        console.log('📱 Tab returning to foreground');
        //! #44: a socket often dies silently while the tab was backgrounded
        //! (laptop sleep, network flap). If we're not healthy on return, kick an
        //! immediate reconnect instead of waiting out the backoff timer.
        if (WebSocketManager.connectionState !== 'connected' || !WebSocketManager.isAuthenticated) {
          WebSocketManager.forceReconnectNow('visibility-foreground');
        }
        //! #50/R6: return value intentionally unused here — `useChartData` reads
        //! the same duration off the `tab_foregrounded` event this call emits.
        WebSocketManager.markForegrounded();

        //! V12B-05: Chrome's intensive throttling stretches the 10s delivery-stall
        //! watchdog to ~1/min in a hidden tab (measured: watchdog.runs advanced
        //! 9 -> 10 across 4 hidden minutes). So the "~45s detection" guarantee holds
        //! ONLY while foregrounded; hidden, worst case is minutes — and the moment
        //! that actually matters is a user coming back to a frozen screen, which is
        //! exactly THIS event. Re-evaluate delivery health here instead of waiting
        //! out a throttled interval.
        //!
        //! Deliberately DELAYED by a short drain window rather than evaluated
        //! inline: a tab returning from background has queued frames still to
        //! process, so lastInboundTickAt can read stale for a few hundred ms purely
        //! as an artefact of the throttle. Checking immediately would flash a false
        //! "Data paused" at the user at the exact moment we are trying to earn their
        //! trust. The grace costs ~1.5s against a worst case of ~60s.
        if (visibilityRecheckRef.current) clearTimeout(visibilityRecheckRef.current);
        visibilityRecheckRef.current = setTimeout(() => {
          visibilityRecheckRef.current = null;
          setConnectionHealth(resolveHealth);
        }, VISIBILITY_RECHECK_DELAY_MS);

        //! #50/R6 (CLOSED): a long absence leaves a real hole — WebSocket is
        //! live-only with no replay, so a socket that died while hidden (a
        //! throttled heartbeat, laptop sleep, a network flap) loses every tick
        //! in that window and resumes streaming from `now`. This provider stays
        //! the ONE owner of the visibility event and does no repair itself —
        //! `markForegrounded()` above already emits `tab_foregrounded` with
        //! `{backgroundDuration, topics}`, and `useChartData` (own-topic,
        //! live/paper only, >60s gate) is the sole consumer that refetches the
        //! exact missed range via `rawHistoryRef`. Do not add a second listener
        //! or a second refetch path here — see decommissioned
        //! `useVisibilityRefresh` (deleted; it had zero importers).
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityRecheckRef.current) {
        clearTimeout(visibilityRecheckRef.current);
        visibilityRecheckRef.current = null;
      }
    };
  }, []); // Empty deps - WebSocketManager is a singleton

  /**
   * #44 (b): auto-resume on network/focus recovery. The browser fires `online`
   * when connectivity returns and `focus` when the window is refocused — both
   * are the moments a silently-dead socket should reconnect AT ONCE rather than
   * wait out the (up to 30s) slow-phase timer.
   */
  useEffect(() => {
    const resumeIfUnhealthy = (reason) => {
      if (WebSocketManager.connectionState !== 'connected' || !WebSocketManager.isAuthenticated) {
        console.log(`🌐 [WS auto-resume] ${reason} → forcing immediate reconnect`);
        WebSocketManager.forceReconnectNow(reason);
      }
    };
    const handleOnline = () => {
      // navigator is back online — clear the "offline" banner state and reconnect.
      setConnectionHealth((prev) => (prev === 'offline' ? 'reconnecting' : prev));
      resumeIfUnhealthy('online');
    };
    const handleOffline = () => {
      console.warn('🌐 [WS] browser reports offline');
      setConnectionHealth('offline');
    };
    const handleFocus = () => resumeIfUnhealthy('focus');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('focus', handleFocus);
    // Seed initial offline state (e.g. app opened with no connectivity).
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setConnectionHealth('offline');
    }
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', handleFocus);
    };
  }, []); // singleton manager

  /**
   * #44 V12 (d): delivery-stall watchdog. Catches the "backend green / screen
   * blank" class — transport CONNECTED + authenticated but zero ticks reaching
   * subscribers. getDeliveryHealth() reports `stalled` only when ticks WERE
   * flowing this session, an active tick subscription has gone silent, AND a
   * venue is currently trading.
   *
   * //! V12b F2: that last conjunct is load-bearing, not decoration. The
   * //! "self-calibrating, never false-fires off-market-hours" claim this
   * //! docstring used to make held ONLY for a session that started off-hours
   * //! (lastInboundTickAt stays null). For the common case — a tab open ACROSS
   * //! the close — the tick age grew without bound and the predicate was
   * //! permanently true. Combined with the banner now persisting, that pinned
   * //! "Data paused" all night and fired a browser notification. Hence the
   * //! session gate AND the baseline reset below.
   *
   * On a stall we surface the banner, emit a structured diag signal, and
   * self-heal a BOUNDED number of times per episode (see MAX_HEALS_PER_EPISODE).
   *
   * //! V12b F1: the heal used to be a no-op — forceReconnectNow refuses to act
   * //! on a connected+authenticated socket, which is precisely what a delivery
   * //! stall is. It is now called with force:true. Do not remove that flag.
   */
  //! V12b observability: the watchdog is SILENT while healthy (it early-returns on
  //!  !stalled), so "no STALL in the console" is indistinguishable from "the watchdog
  //!  never ran / is gated off by hasActiveTickSubscriptions()". A control run needs
  //!  POSITIVE proof of liveness, so every pass records a beat here.
  const watchdogBeatRef = useRef({ runs: 0, stalls: 0, heals: 0, lastRunAt: null, lastHealth: null });

  useEffect(() => {
    const HEAL_COOLDOWN_MS = 60000;   // at most one self-heal reconnect per 60s
    //! V12b F1/F8: the heal is BOUNDED. A client reconnect can only repair a
    //! client-side cause (a subscription desync). It cannot make a dead broker
    //! adapter produce ticks — the actual cause of the 2026-08-04 outage — so an
    //! unbounded 60s heal loop would rebuild every tab's socket all through a
    //! long upstream outage and never fix anything. Try a few times, then stop
    //! reconnecting and just keep telling the user the truth via the banner.
    const MAX_HEALS_PER_EPISODE = 3;
    //! The budget resets only after SUSTAINED recovery, not after a single
    //! healthy pass. A feed delivering ticks just over the 45s threshold
    //! oscillates stalled -> healthy -> stalled, and a one-pass reset let it
    //! earn a fresh budget every cycle — so the bound was escapable and the
    //! churn unbounded (only 60s-rate-limited). That matters now that a heal
    //! really does tear the socket down and force a resubscribe.
    const HEALTHY_PASSES_TO_RESET_BUDGET = 6; // 6 x 10s = 60s of clean delivery
    let lastHealAt = 0;
    let healsThisEpisode = 0;
    let consecutiveHealthy = 0;

    const interval = setInterval(() => {
      const health = WebSocketManager.getDeliveryHealth(DELIVERY_STALL_THRESHOLD_MS);

      // --> beat: proves the watchdog is alive even when it has nothing to report
      const beat = watchdogBeatRef.current;
      beat.runs += 1;
      beat.lastRunAt = Date.now();
      beat.lastHealth = health;

      //! V12b F2: the trading day ended. Drop the baseline so tonight's silence
      //! is not read as a fault and so tomorrow's open re-calibrates from the
      //! first REAL tick instead of from yesterday's last one.
      if (!health.sessionOpen) {
        if (WebSocketManager.resetDeliveryBaseline('session-closed')) {
          beat.baselineResets = (beat.baselineResets || 0) + 1;
        }
        healsThisEpisode = 0;
        return;
      }

      if (!health.stalled) {
        // Recovered (or never stalled) — let the normal connected path clear UI.
        consecutiveHealthy += 1;
        if (consecutiveHealthy >= HEALTHY_PASSES_TO_RESET_BUDGET) {
          healsThisEpisode = 0; //! sustained recovery — a NEW stall gets a fresh budget
          watchdogBeatRef.current.gaveUpLogged = false;
        }
        return;
      }
      consecutiveHealthy = 0;
      beat.stalls += 1;
      //! structured, greppable diag — the frontend half of the V12 signal.
      console.error('[WS-DELIVERY] STALL: transport up but no ticks reaching subscribers', {
        lastTickAgeMs: health.lastTickAgeMs,
        tickSubCount: health.tickSubCount,
        thresholdMs: health.stallThresholdMs,
      });
      //! same ground-truth resolver as every other writer — `stalled` is already
      //! true here, so this yields 'stalled' unless the manager reports something
      //! louder. Using the shared ladder is what keeps the writers from
      //! disagreeing, which is the root cause this whole pipeline exists for.
      setConnectionHealth(resolveHealth);

      // Self-heal: force one reconnect (replays subscriptions) per cooldown.
      const nowTs = Date.now();
      if (nowTs - lastHealAt > HEAL_COOLDOWN_MS && healsThisEpisode < MAX_HEALS_PER_EPISODE) {
        lastHealAt = nowTs;
        healsThisEpisode += 1;
        beat.heals += 1;
        console.warn(`[WS-DELIVERY] self-healing: forcing reconnect to restore tick delivery (${healsThisEpisode}/${MAX_HEALS_PER_EPISODE})`);
        //! force:true — a delivery stall is BY DEFINITION connected+authenticated,
        //! which is exactly what forceReconnectNow's default guard refuses to act
        //! on. Without this the heal was a silent no-op (V12b F1).
        //! preserveBackoff:true — do not keep resetting the retry budget, or the
        //! escalation to 'degraded' can never be reached (V12b F8).
        WebSocketManager.forceReconnectNow('delivery-stall', { force: true, preserveBackoff: true });
      } else if (healsThisEpisode >= MAX_HEALS_PER_EPISODE && !beat.gaveUpLogged) {
        //! Operator-facing: without this an observer cannot tell "waiting out the
        //! 60s cooldown" from "gave up and is only reporting now". Logged once
        //! per episode so a long outage does not spam.
        beat.gaveUpLogged = true;
        console.warn(`[WS-DELIVERY] heal budget exhausted (${MAX_HEALS_PER_EPISODE}) — no further reconnects this episode; the banner stays up until delivery returns`);
      }
    }, 10000); // check every 10s

    return () => clearInterval(interval);
  }, []); // singleton manager

  /**
   * #31/exchange-session-ssot R5, R5.1: REST seed for the MDMS-fed exchange-status
   * store (`exchangeHours.js`). One-shot, idempotent (the module dedups concurrent
   * callers) — fires once regardless of WS state so isExchangeOpenNow etc. have an
   * answer as soon as possible after mount, not gated on auth/subscribe completing.
   */
  useEffect(() => {
    loadExchangeStatusFromApi();
  }, []);

  /**
   * #31/exchange-session-ssot R5.2 (DK-4): the ONE `exchange-status.update`
   * subscription for the whole app. Feeds the shared exchangeHours.js store (so
   * isExchangeOpenNow/isTickVenueOpenNow/isAnyIndianVenueOpenNow reflect live MDMS
   * pushes) AND fans the raw payload out via context (`lastExchangeStatusUpdate`)
   * for consumers that render the full status row (e.g. MarketStatusDashboard),
   * replacing that component's own now-deleted local subscription.
   *
   * Gated on isWsReady (not isConnected) per this file's own convention — see the
   * isWsReady declaration comment — so subscribeTopic only fires once
   * WebSocketManager.isAuthenticated is actually true.
   */
  useEffect(() => {
    if (!isWsReady) return undefined;

    const unsubscribe = WebSocketManager.subscribeTopic('exchange-status.update', (message) => {
      const data = message?.d || message?.data || message;
      if (!data || !data.exchange) return;
      updateExchangeStatus(data.exchange, data);
      setLastExchangeStatusUpdate({ ...data, receivedAt: Date.now() });
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [isWsReady]);

  /**
   * V12b/#44 diagnostic hook — READ-ONLY, non-production only.
   * Exposes the delivery-health snapshot the stall watchdog gates on, plus the
   * watchdog's own beat counters, so a control run can distinguish "healthy and
   * watching" from "not running at all". Mutates nothing.
   */
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    window.__wsHealth = () => ({
      ...WebSocketManager.getDeliveryHealth(),
      watchdog: { ...watchdogBeatRef.current },
      at: new Date().toISOString(),
    });
    console.info('[DIAG][WS-DELIVERY] window.__wsHealth() armed');
    return () => { delete window.__wsHealth; };
  }, []);

  /**
   * #44: health RECONCILER + last-resort rescue. Events (auth_success / online)
   * can be missed or, in some environments (e.g. DevTools network emulation),
   * not fire at all — which left the socket reconnected but the banner pinned on
   * "reconnecting". So instead of trusting events alone we reconcile the banner
   * against the manager's ground-truth every 2s:
   *   (1) truly connected+authenticated AND delivering -> force the banner cleared;
   *   (2) manager reports itself STUCK  -> rescue it with ONE forced reconnect.
   *
   * (1) is deliberately gated on delivery health as well as transport state. A
   * socket that is connected and authenticated but receiving no ticks is NOT
   * healthy — see the V12b note inline below.
   *
   * (2) is deliberately narrow. The first cut nudged whenever we weren't
   * connected+authed, which tore sockets down mid-auth-handshake and reset the
   * backoff every 8s — it never stabilised. `WebSocketManager.needsRescue()` now
   * owns that judgement: it stays silent while the manager's own connectTimeout,
   * backoff timer, or auth-grace window has the situation in hand, and returns a
   * reason only when nothing at all is left to recover the socket. The
   * RESCUE_INTERVAL floor is the second belt — even a wrong "stuck" verdict can
   * cost at most one reconnect per 30s, never a storm.
   */
  useEffect(() => {
    const RESCUE_INTERVAL_MS = 30000;
    let lastRescue = 0;
    const id = setInterval(() => {
      const m = WebSocketManager;
      if (m.connectionState === 'connected' && m.isAuthenticated) {
        //! TRANSPORT ground truth says healthy — but transport-up is NOT the same
        //! as healthy, and conflating the two is the V12b defect (2026-08-04).
        //! During a delivery stall the socket IS connected and authenticated —
        //! that is the *definition* of the failure class — so clearing here
        //! unconditionally overwrote the watchdog's 'stalled' within <=2s. The
        //! "Data paused" pill could never survive long enough to be seen: the
        //! watchdog detected and self-healed correctly while its entire
        //! user-visible purpose was silently erased. Delivery health, not
        //! transport state, decides whether this branch may clear the banner.
        //! (This is still the path that CLEARS 'stalled' on recovery — the
        //!  watchdog early-returns when healthy and relies on exactly that.)
        //! V12b F3: routed through the shared precedence ladder rather than an
        //! inline `if (!stalled)`. Besides de-duplicating the rule, this also
        //! repairs a stale 'reconnecting' banner DURING a stall in one 2s pass
        //! instead of leaving the wrong copy up until the 10s watchdog
        //! overwrites it (up to ~60s in a throttled background tab).
        setConnectionHealth(resolveHealth);
        //! V12b F6: the reconciler exists PRECISELY because auth_success can be
        //! missed — but hasConnectedOnce was only ever set in that handler, and
        //! LiveDataStatus suppresses the stall pill entirely while it is false.
        //! So in exactly the session this reconciler was built for, the banner
        //! this pipeline exists to surface was silently suppressed for ever.
        //! Ground truth says we are connected+authenticated: record it.
        setHasConnectedOnce((prev) => (prev ? prev : true));
        setReconnectError((prev) => (prev ? null : prev));
        return;
      }
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return; //! known-offline — the `online` listener owns the resume
      }
      const now = Date.now();
      if (now - lastRescue < RESCUE_INTERVAL_MS) return;

      const reason = m.needsRescue(now);
      if (reason) {
        lastRescue = now;
        console.warn(`🛟 [WS rescue] manager stuck (${reason}) — forcing one reconnect`);
        m.forceReconnectNow(`poll-rescue:${reason}`);
      }
    }, 2000);
    return () => clearInterval(id);
  }, []); // singleton manager

  /**
   * Get connection diagnostics
   */
  const getDiagnostics = useCallback(() => {
    return {
      connectionState,
      isConnected,
      error,
      userIdentity,
      globalMetrics,
      connectionConfig,
      wsManagerState: WebSocketManager.getState()
    };
  }, [connectionState, isConnected, error, userIdentity, globalMetrics, connectionConfig]);

  /**
   * Send global message (not page-specific)
   */
  const sendGlobalMessage = useCallback((message, channel = 'global') => {
    if (!isConnected) {
      console.warn('Cannot send global message: WebSocket not connected');
      return false;
    }

    try {
      WebSocketManager.sendMessage({
        ...message,
        global: true,
        timestamp: performance.now()
      }, channel);
      return true;
    } catch (error) {
      console.error('Failed to send global message:', error);
      setError(error);
      return false;
    }
  }, [isConnected]);

  /**
   * Subscribe to global events
   */
  const subscribeGlobal = useCallback((topic, handler, options = {}) => {
    // Route global subscriptions through topic-aware API so onData/onAck/onError
    // handlers receive structured subscription events.
    return WebSocketManager.subscribeTopic(topic, handler, options);
  }, []);

  // Context value
  const contextValue = {
    // Connection state
    connectionState,
    isConnected,
    isWsReady,
    error,
    userIdentity,
    globalMetrics,
    connectionConfig,
    
    // Connection methods
    connect,
    disconnect,
    initializeConnection,
    
    // Configuration
    autoConnect,
    updateConfig,
    
    // Global messaging
    sendGlobalMessage,
    subscribeGlobal,
    
    // Utilities
    getDiagnostics,
    
    // WebSocket manager access (for advanced usage)
    webSocketManager: WebSocketManager,

    // Reconnect error
    reconnectError,
    //! #44: connection-health surface for the LiveDataStatus + any consumer that
    //! wants to reflect live-data status. 'connected'|'reconnecting'|'degraded'|
    //! 'stalled'|'offline'. `hasConnectedOnce` gates first-load banner flash.
    connectionHealth,
    hasConnectedOnce,
    getDeliveryHealth: () => WebSocketManager.getDeliveryHealth(),

    //! #31/exchange-session-ssot R5.2: the ONE `exchange-status.update` subscription's
    //! latest payload, fanned out to every consumer via context — see the subscription
    //! effect above. `null` until the first frame arrives this session.
    lastExchangeStatusUpdate,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket context
 */
export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to access WebSocket connection state only
 */
export function useWebSocketState() {
  const context = useWebSocketContext();
  return {
    connectionState: context.connectionState,
    isConnected: context.isConnected,
    error: context.error,
    metrics: context.globalMetrics
  };
}

/**
 * Hook to access user identity from WebSocket context
 */
export function useWebSocketUser() {
  const context = useWebSocketContext();
  return {
    userIdentity: context.userIdentity,
    userId: context.userIdentity?.user_id,
    sessionId: context.userIdentity?.session_id,
    deviceId: context.userIdentity?.device_id,
    windowId: context.userIdentity?.window_id
  };
}

/**
 * Hook for global WebSocket messaging
 */
export function useGlobalWebSocket() {
  const context = useWebSocketContext();
  return {
    isConnected: context.isConnected,
    isWsReady: context.isWsReady,
    sendGlobalMessage: context.sendGlobalMessage,
    subscribeGlobal: context.subscribeGlobal,
    metrics: context.globalMetrics
  };
}

/**
 * Hook to access the provider's single `exchange-status.update` fan-out.
 * #31/exchange-session-ssot R5.2 — consumers that need the raw per-exchange
 * status row (MarketStatusDashboard) read this instead of subscribing to the
 * WS topic themselves.
 */
export function useExchangeStatusUpdate() {
  const context = useWebSocketContext();
  return context.lastExchangeStatusUpdate;
}

/**
 * Higher-order component to provide WebSocket context
 */
export function withWebSocket(WrappedComponent) {
  return function WebSocketWrappedComponent(props) {
    return (
      <WrappedComponent {...props} webSocket={useWebSocketContext()} />
    );
  };
}

export default WebSocketProvider;
